import { assertPublicSiteCapabilities, executePublicRouteLoader, normalizePublicRouteDataPath, resolvePublicLoaderCanonicalPath, } from "../../core/index.js";
import { extendPublicHeadDescriptor } from "../../seo/index.js";
export function createPublicHydrationLifecycle(options) {
    let phase = "pre-hydration";
    let pendingUrl = null;
    const activate = async (url) => {
        try {
            return await options.activate(url);
        }
        catch (error) {
            options.onError?.(error);
            throw error;
        }
    };
    return {
        get phase() {
            return phase;
        },
        get pendingUrl() {
            return pendingUrl;
        },
        navigate(url) {
            if (phase === "disposed")
                return Promise.resolve(null);
            if (phase === "pre-hydration") {
                pendingUrl = url;
                return Promise.resolve(null);
            }
            return activate(url);
        },
        completeHydration(fallbackUrl) {
            if (phase !== "pre-hydration")
                return Promise.resolve(null);
            const url = pendingUrl ?? fallbackUrl;
            pendingUrl = null;
            phase = "hydrated";
            return activate(url);
        },
        dispose() {
            pendingUrl = null;
            phase = "disposed";
        },
    };
}
function getReadyExpiration(resolution, now) {
    const delivery = resolution.route.delivery;
    if (delivery.kind !== "dynamic" || delivery.cache.kind === "no-store")
        return undefined;
    return now + delivery.cache.maxAgeSeconds * 1_000;
}
function isAbortError(error) {
    return error instanceof DOMException && error.name === "AbortError";
}
function canReuseInitialState(state, policy, now) {
    if (state.status === "not-found")
        return true;
    if (state.status !== "ready")
        return false;
    if (policy !== "revalidate-if-expired")
        return true;
    if (state.expiresAt === undefined)
        return true;
    return state.expiresAt > now;
}
function getReusableDynamicState(state, isInitial, policy, now) {
    if (!state)
        return undefined;
    if (isInitial) {
        if (!canReuseInitialState(state, policy, now))
            return undefined;
        return state;
    }
    if (state.status !== "ready")
        return undefined;
    if (state.expiresAt === undefined)
        return undefined;
    if (state.expiresAt <= now)
        return undefined;
    return state;
}
export function createPublicBrowserRuntime(options) {
    assertPublicSiteCapabilities(options.site, {
        loaders: options.loaders,
        headResolvers: options.headResolvers,
    });
    const maximumEntries = options.maximumEntries ?? 50;
    if (!Number.isSafeInteger(maximumEntries) || maximumEntries < 1)
        throw new Error("maximumEntries must be a positive safe integer.");
    const states = new Map();
    const pending = new Map();
    const listeners = new Set();
    const initialPaths = new Set();
    let activeController = null;
    let activePath = null;
    if (options.initialState) {
        const path = normalizePublicRouteDataPath(options.initialState.path);
        states.set(path, {
            ...options.initialState,
            path,
        });
        initialPaths.add(path);
    }
    const touch = (path, state) => {
        states.delete(path);
        states.set(path, state);
        while (states.size > maximumEntries) {
            const oldestPath = states.keys().next().value;
            if (!oldestPath)
                break;
            states.delete(oldestPath);
        }
    };
    const resolveStateHead = (state) => {
        const resolution = options.site.resolvePath(state.path);
        const canonicalPath = state.status === "ready" || state.status === "loading"
            ? state.canonicalPath ?? state.path
            : state.path;
        const headData = state.status === "ready" || state.status === "loading"
            ? state.data
            : undefined;
        if (state.status === "not-found")
            return options.resolveNotFoundHead?.(state.path)
                ?? options.resolveHead(state.path);
        if (state.status === "unavailable")
            return options.resolveUnavailableHead?.(state.path)
                ?? options.resolveHead(state.path);
        if (resolution.type === "render"
            && resolution.route.headResolverKey
            && headData) {
            const resolver = options.headResolvers?.[resolution.route.headResolverKey];
            if (!resolver)
                throw new Error(`Missing public head resolver '${resolution.route.headResolverKey}'.`);
            return extendPublicHeadDescriptor(options.resolveHead(canonicalPath), resolver({
                pathname: resolution.routePath,
                canonicalPath,
                route: resolution.route,
                params: resolution.params,
                data: headData,
            }));
        }
        return options.resolveHead(canonicalPath);
    };
    const publish = (state) => {
        touch(state.path, state);
        options.applyHead(resolveStateHead(state));
        for (const listener of listeners)
            listener(state);
    };
    const getState = (rawPath) => {
        const path = normalizePublicRouteDataPath(rawPath);
        return states.get(path) ?? {
            status: "idle",
            path,
        };
    };
    const runActivation = async (path) => {
        const resolution = options.site.resolvePath(path);
        if (activePath !== path) {
            activeController?.abort();
            activeController = null;
            activePath = path;
        }
        if (resolution.type !== "render" || resolution.route.delivery.kind !== "dynamic") {
            const state = resolution.type === "not-found"
                ? {
                    status: "not-found",
                    path,
                }
                : {
                    status: "ready",
                    path,
                    data: {},
                };
            publish(state);
            return state;
        }
        const existing = states.get(path);
        const now = Date.now();
        const isInitial = initialPaths.delete(path);
        const reusableState = getReusableDynamicState(existing, isInitial, options.initialStatePolicy, now);
        if (reusableState) {
            try {
                publish(reusableState);
                return reusableState;
            }
            catch (error) {
                options.onError?.(error);
                const state = {
                    status: "unavailable",
                    path,
                };
                publish(state);
                return state;
            }
        }
        activeController?.abort();
        const controller = new AbortController();
        activeController = controller;
        const loadingState = {
            status: "loading",
            path,
            ...(existing?.status === "ready"
                ? {
                    data: existing.data,
                    canonicalPath: existing.canonicalPath,
                }
                : {}),
        };
        publish(loadingState);
        try {
            const result = await executePublicRouteLoader(resolution, options.loaders, {
                signal: controller.signal,
                url: path,
            });
            if (controller.signal.aborted || activePath !== path)
                return getState(path);
            switch (result.kind) {
                case "ok": {
                    const canonicalPath = resolvePublicLoaderCanonicalPath(options.site, resolution, result.canonicalPath);
                    const generatedAt = Date.now();
                    const state = {
                        status: "ready",
                        path,
                        data: result.data,
                        ...(canonicalPath === path ? {} : { canonicalPath }),
                        generatedAt,
                        expiresAt: getReadyExpiration(resolution, generatedAt),
                    };
                    publish(state);
                    return state;
                }
                case "not-found": {
                    const state = {
                        status: "not-found",
                        path,
                    };
                    publish(state);
                    return state;
                }
                case "unavailable": {
                    const state = {
                        status: "unavailable",
                        path,
                        retryAfter: result.retryAfter,
                    };
                    publish(state);
                    return state;
                }
                case "redirect": {
                    const state = {
                        status: "redirect",
                        path,
                        location: result.location,
                    };
                    publish(state);
                    options.onRedirect?.(result.location);
                    return state;
                }
            }
        }
        catch (error) {
            if (controller.signal.aborted || isAbortError(error))
                return getState(path);
            options.onError?.(error);
            const state = {
                status: "unavailable",
                path,
            };
            publish(state);
            return state;
        }
    };
    const activate = (rawPath) => {
        const path = normalizePublicRouteDataPath(rawPath);
        const existing = pending.get(path);
        if (activePath === path && existing)
            return existing;
        const operation = runActivation(path);
        const clearPending = () => {
            if (pending.get(path) === operation)
                pending.delete(path);
        };
        pending.set(path, operation);
        void operation.then(clearPending, clearPending);
        return operation;
    };
    return {
        activate,
        getState,
        subscribe(listener) {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
        dispose() {
            activeController?.abort();
            listeners.clear();
            pending.clear();
            states.clear();
        },
    };
}
//# sourceMappingURL=index.js.map